import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Languages, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface TranslateFieldButtonProps {
  children: ReactNode;
  /** Invoked when the translate icon is pressed. */
  onTranslate: () => void;
  /** Enabled only when the primary-language field has a value. */
  enabled: boolean;
  busy?: boolean;
}

/**
 * The in-field translate trigger: an icon pinned inside and to the inline-end
 * of the input, enabled only when the primary-language field has a value, and
 * translating that value into the current field on press (README section
 * 0.1.2, "translation button"). Uses `end-*` logical utilities so the icon
 * mirrors automatically under RTL.
 */
export function TranslateFieldButton({
  children,
  onTranslate,
  enabled,
  busy = false,
}: TranslateFieldButtonProps) {
  const { t } = useTranslation();
  return (
    <div className="relative">
      {children}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="absolute end-1.5 top-1/2 h-6 w-6 -translate-y-1/2"
            disabled={!enabled || busy}
            onClick={onTranslate}
            aria-label={t("translations.translate")}
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Languages className="h-3.5 w-3.5" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("translations.translate")}</TooltipContent>
      </Tooltip>
    </div>
  );
}
